import { useSessionKey } from "@auth/sessionStore";
import { CompanionPanel } from "@components/CompanionPanel";
import {
  EmptyNote,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  PanelError,
  Skeleton,
  Tag,
} from "@scribenote/brian-design-system";
import { AddUserButton } from "@users/AddUserButton";
import { EditRolesButton } from "@users/EditRolesButton";
import { RemoveUserButton } from "@users/RemoveUserButton";
import { ResetPinButton } from "@users/ResetPinButton";
import { type ClinicUser, isLeadershipRole } from "@users/userClient";
import { useUsersList } from "@users/userQueries";
import { formatDate } from "@utils/format-date";

const UserRow = ({
  user,
  sessionKey,
}: {
  user: ClinicUser;
  sessionKey: string;
}) => (
  <LedgerRow data-testid="admin-user-row">
    <LedgerCell>
      <span className="block max-w-[20rem] truncate font-medium text-ink">
        {user.displayName}
      </span>
    </LedgerCell>
    <LedgerCell>
      <span className="flex max-w-[16rem] flex-wrap items-center gap-1">
        {user.roles.map((role) => (
          <Tag
            key={role.id}
            tone={isLeadershipRole(role) ? "emphasis" : "neutral"}
          >
            {role.name}
          </Tag>
        ))}
      </span>
    </LedgerCell>
    <LedgerCell mono className="text-annotation">
      {user.code}
    </LedgerCell>
    <LedgerCell mono className="whitespace-nowrap text-annotation">
      {formatDate(user.createdAt)}
    </LedgerCell>
    <LedgerCell align="right" className="w-px whitespace-nowrap">
      <span className="inline-flex items-center gap-2">
        <EditRolesButton user={user} sessionKey={sessionKey} />
        <ResetPinButton user={user} />
        <RemoveUserButton user={user} sessionKey={sessionKey} />
      </span>
    </LedgerCell>
  </LedgerRow>
);

const UsersList = ({ sessionKey }: { sessionKey: string }) => {
  const { data: users } = useUsersList(sessionKey);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <AddUserButton sessionKey={sessionKey} />
      </div>
      {users.length === 0 ? (
        <EmptyNote>Nobody on the roster yet.</EmptyNote>
      ) : (
        <LedgerTable data-testid="admin-user-list">
          <LedgerHead>
            <tr>
              <LedgerHeadCell>Name</LedgerHeadCell>
              <LedgerHeadCell>Roles</LedgerHeadCell>
              <LedgerHeadCell>ID</LedgerHeadCell>
              <LedgerHeadCell>Joined</LedgerHeadCell>
              <LedgerHeadCell align="right" className="w-px">
                Actions
              </LedgerHeadCell>
            </tr>
          </LedgerHead>
          <LedgerBody>
            {users.map((u) => (
              <UserRow key={u.id} user={u} sessionKey={sessionKey} />
            ))}
          </LedgerBody>
        </LedgerTable>
      )}
    </div>
  );
};

const UsersSkeleton = () => (
  <div className="overflow-hidden rounded-paper border border-rule bg-card">
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 border-b border-rule/50 p-3 last:border-b-0 even:bg-[color-mix(in_srgb,var(--color-wash-working)_16%,transparent)]"
      >
        <Skeleton index={i} className="h-4 flex-1" />
        <Skeleton index={i} className="h-4 w-24" />
      </div>
    ))}
  </div>
);

export const UsersPanel = () => {
  const sessionKey = useSessionKey();

  return (
    <CompanionPanel
      skeleton={<UsersSkeleton />}
      fallback={(retry) => <PanelError label="users" onRetry={retry} />}
    >
      <UsersList sessionKey={sessionKey} />
    </CompanionPanel>
  );
};
